import { useMemo } from "react";
import { AttributeComparisonSummarySection } from "./AttributeComparisonSummarySection";
import { AttributeSummaryTable } from "./charts/AttributeSummaryTable";
import { AttributeComparisonSummaryProps } from "./types";

import * as ATTRS from "./OutputAreaAttributesUK";
import { HorizontalBarChart } from "./charts/HorizontalBarChart";
import { useDemographicsColourAccessor } from "./charts/colorUtils";
import { hasAnyRequiredAttribute } from "./utils";
import { NationalDensityComparisonChart } from "./charts/NationalDensityComparisonChart";
import { Alert } from "react-migration/components/Alert";
import { ConstraintsCategory } from "react-migration/layouts/map/Constraints/types";
import { LinearTrendChart } from "./charts/LinearTrendChart";
import { SingleDesignation } from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import { isDefined } from "react-migration/lib/util/isDefined";
import clsx from "clsx";

/* TODO:
- Resolve comments
- Align ordering + naming between map and attributes.
- Deprivation on map, but missing from attributes
- Property tenure in attributes, but missing from map.
*/

const POPULATION_GROWTH_DATA_POINTS = [
  {
    value: 0,
    xValue: 2024,
  },
  ATTRS.POPULATION_CHANGE_5YR,
  ATTRS.POPULATION_CHANGE_10YR,
];

const POPULATION_GROWTH_65_PLUS_DATA_POINTS = [
  {
    value: 0,
    xValue: 2024,
  },
  ATTRS.POPULATION_CHANGE_65_5YR,
  ATTRS.POPULATION_CHANGE_65_10YR,
];

export function AttributeComparisonSummaryUK({
  outputArea,
  comparableDesignation,
  allOutputAreas,
  printable = false,
  hiddenSections = [],
}: AttributeComparisonSummaryProps & { hiddenSections?: string[] }) {
  const colorAccessor = useDemographicsColourAccessor(outputArea, comparableDesignation);

  const sections = useMemo(
    () => ({
      affordability: hasAnyRequiredAttribute(ATTRS.AFFORDABILITY_ATTRS, outputArea),
      age: hasAnyRequiredAttribute(ATTRS.AGE_ATTRS, outputArea),
      dwellingAge: hasAnyRequiredAttribute(ATTRS.DWELLING_AGE_ATTRS, outputArea),
      dwellingDensity: hasAnyRequiredAttribute(ATTRS.DWELLING_DENSITY_ATTRS, outputArea),
      employment: hasAnyRequiredAttribute(ATTRS.EMPLOYMENT_ATTRS, outputArea),
      income: hasAnyRequiredAttribute(ATTRS.INCOME_ATTRS, outputArea),
      occupancy: hasAnyRequiredAttribute(ATTRS.OCCUPANCY_ATTRS, outputArea),
      populationDensity: hasAnyRequiredAttribute(ATTRS.POPULATION_DENSITY_ATTRS, outputArea),
      populationGrowth: hasAnyRequiredAttribute(ATTRS.POPULATION_CHANGE_ATTRS, outputArea),
      propertyTenure: hasAnyRequiredAttribute(ATTRS.PROPERTY_TENURE_ATTRS, outputArea),
    }),
    [outputArea]
  );

  const { countryDesignation } = useMemo(
    () => ({
      countryDesignation: allOutputAreas.find(
        (oa) => oa.sub_category_id === ConstraintsCategory.UK_DEMOGRAPHICS_COUNTRY
      )!,
    }),
    [allOutputAreas]
  );

  const populationGrowthOutputAreas = useMemo(
    () => [outputArea, comparableDesignation, countryDesignation].filter(isDefined),
    [outputArea, comparableDesignation, countryDesignation]
  );

  const affordabilityOutputAreas = useMemo(
    () => [outputArea, comparableDesignation].filter(isDefined),
    [outputArea, comparableDesignation]
  );

  return (
    <>
      {sections.age && !hiddenSections.includes("age") && (
        <AttributeComparisonSummarySection
          heading="Age"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.AGE_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            printable={printable}
            attributes={ATTRS.AGE_ATTRS.slice(0, 2)}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
          />
          <HorizontalBarChart
            printable={printable}
            attributes={ATTRS.AGE_ATTRS.slice(2)}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
          />
        </AttributeComparisonSummarySection>
      )}
      {/* No data available above LSOA + MSOA*/}
      {!hiddenSections.includes("populationDensity") && (
        <AttributeComparisonSummarySection
          heading="Population Density"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.POPULATION_DENSITY_ATTRS[0]}
          printable={printable}
        >
          {sections.populationDensity && (
            <AttributeSummaryTable
              printable={printable}
              attributes={ATTRS.POPULATION_DENSITY_ATTRS.slice(0, 1)}
              designation={outputArea}
              comparativeDesignation={comparableDesignation}
              colorAccessor={colorAccessor}
            />
          )}
          <NationalDensityComparisonChart
            printable={printable}
            chartedAttribute={ATTRS.POPULATION_DENSITY_ATTRS[0]}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            countryDesignation={countryDesignation}
            countryAttributes={ATTRS.POPULATION_DENSITY_ATTRS.slice(1)}
            colorAccessor={colorAccessor}
          />
          {!sections.populationDensity && !printable && (
            <Alert.Info>Population density available for MSOA & LSOA only.</Alert.Info>
          )}
        </AttributeComparisonSummarySection>
      )}

      {!hiddenSections.includes("populationGrowth") && (
        <AttributeComparisonSummarySection
          heading="Population Growth"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.POPULATION_CHANGE_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            printable={printable}
            attributes={ATTRS.POPULATION_CHANGE_ATTRS.slice(0, 2)}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
          />
          <LinearTrendChart
            printable={printable}
            dataPoints={POPULATION_GROWTH_DATA_POINTS}
            outputAreas={populationGrowthOutputAreas}
            labelY="Change (%)"
            colorAccessor={colorAccessor}
          />

          <AttributeSummaryTable
            printable={printable}
            attributes={ATTRS.POPULATION_CHANGE_ATTRS.slice(2)}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
          />
          <LinearTrendChart
            printable={printable}
            dataPoints={POPULATION_GROWTH_65_PLUS_DATA_POINTS}
            outputAreas={populationGrowthOutputAreas}
            labelY="Change (%)"
            colorAccessor={colorAccessor}
          />
          {!sections.populationGrowth && !printable && (
            <Alert.Info>Population growth not available for MSOA or LSOA.</Alert.Info>
          )}
        </AttributeComparisonSummarySection>
      )}

      {/* No data available for LSOA */}
      {!hiddenSections.includes("income") && sections.income && (
        <AttributeComparisonSummarySection
          heading="Income"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.INCOME_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            printable={printable}
            attributes={ATTRS.INCOME_ATTRS}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
          />
        </AttributeComparisonSummarySection>
      )}

      {!hiddenSections.includes("affordability") && (
        <AttributeComparisonSummarySection
          heading="Affordability"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.AFFORDABILITY_ATTRS[0]}
          printable={printable}
        >
          {sections.affordability && (
            <AttributeSummaryTable
              printable={printable}
              attributes={ATTRS.AFFORDABILITY_ATTRS.slice(0, 1)}
              designation={outputArea}
              comparativeDesignation={comparableDesignation}
              colorAccessor={colorAccessor}
            />
          )}
          <div
            className={clsx("flex flex-row flex-wrap grow", {
              "print:w-[335px] print:justify-self-start print:origin-left": printable,
            })}
          >
            <span className="min-w-full text-2xs text-text-base-tertiary">
              Affordability ratios calculated by dividing house prices by gross annual workplace-based earnings.
            </span>
            <LinearTrendChart
              printable={printable}
              colorAccessor={colorAccessor}
              outputAreas={affordabilityOutputAreas}
              dataPoints={ATTRS.AFFORDABILITY_ATTRS}
              labelY="Affordability ratio"
            />
          </div>
          {!sections.affordability && !printable && <Alert.Info>Affordability not available for LSOA.</Alert.Info>}
        </AttributeComparisonSummarySection>
      )}

      {!hiddenSections.includes("deprivation") && (
        <DeprivationAttributeSummary
          allOutputAreas={allOutputAreas}
          outputArea={outputArea}
          comparableOutputArea={comparableDesignation}
          printable={printable}
        />
      )}

      {sections.employment && !hiddenSections.includes("employment") && (
        <AttributeComparisonSummarySection
          heading="Employment"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.EMPLOYMENT_ATTRS[0]}
          printable={printable}
        >
          <HorizontalBarChart
            printable={printable}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            attributes={[...ATTRS.EMPLOYMENT_ATTRS].reverse()}
            colorAccessor={colorAccessor}
          />
          <div className="flex flex-col w-full">
            <h2 className="text-xs font-semibold text-neutral-800">{"Occupation"}</h2>
            <HorizontalBarChart
              printable={printable}
              designation={outputArea}
              comparativeDesignation={comparableDesignation}
              attributes={[...ATTRS.OCCUPATION_ATTRS.slice(2)].reverse()}
              colorAccessor={colorAccessor}
            />
          </div>
        </AttributeComparisonSummarySection>
      )}

      {sections.occupancy && !hiddenSections.includes("occupancy") && (
        <AttributeComparisonSummarySection
          heading="Occupancy"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.OCCUPANCY_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            printable={printable}
            attributes={ATTRS.OCCUPANCY_ATTRS.slice(0, 1)}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
          />
          <HorizontalBarChart
            printable={printable}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            attributes={ATTRS.OCCUPANCY_ATTRS.slice(1)}
            colorAccessor={colorAccessor}
          />
        </AttributeComparisonSummarySection>
      )}

      {!hiddenSections.includes("dwellings") && (
        <AttributeComparisonSummarySection
          heading="Dwellings"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.DWELLING_DENSITY_ATTRS[1]}
          printable={printable}
        >
          {sections.dwellingDensity && (
            <AttributeSummaryTable
              printable={printable}
              attributes={ATTRS.DWELLING_DENSITY_ATTRS.slice(0, 2)}
              designation={outputArea}
              comparativeDesignation={comparableDesignation}
              colorAccessor={colorAccessor}
            />
          )}
          <NationalDensityComparisonChart
            printable={printable}
            chartedAttribute={ATTRS.DWELLING_DENSITY_ATTRS[1]}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            countryDesignation={countryDesignation}
            countryAttributes={ATTRS.DWELLING_DENSITY_ATTRS.slice(2)}
            colorAccessor={colorAccessor}
          />
          {!sections.dwellingDensity && !printable && (
            <Alert.Info>Dwelling density available for MSOA & LSOA only.</Alert.Info>
          )}
          <div className="flex flex-col grow">
            <h2 className="text-xs font-semibold text-neutral-800">{"Dwelling types"}</h2>
            <HorizontalBarChart
              printable={printable}
              designation={outputArea}
              comparativeDesignation={comparableDesignation}
              attributes={[...ATTRS.DWELLING_TYPES_ATTRS].reverse()}
              colorAccessor={colorAccessor}
            />
          </div>

          {sections.dwellingAge && (
            <AttributeSummaryTable
              printable={printable}
              attributes={ATTRS.DWELLING_AGE_ATTRS.slice(0, 3)}
              designation={outputArea}
              comparativeDesignation={comparableDesignation}
              colorAccessor={colorAccessor}
            />
          )}
        </AttributeComparisonSummarySection>
      )}

      {sections.propertyTenure && !hiddenSections.includes("propertyTenure") && (
        <AttributeComparisonSummarySection
          heading="Property Tenure"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.PROPERTY_TENURE_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            printable={printable}
            attributes={ATTRS.PROPERTY_TENURE_ATTRS}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
          />
          <HorizontalBarChart
            printable={printable}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            attributes={[...ATTRS.PROPERTY_TENURE_ATTRS].reverse()}
            colorAccessor={colorAccessor}
          />
        </AttributeComparisonSummarySection>
      )}
    </>
  );
}

interface DeprivationAttributeSummaryProps {
  allOutputAreas: SingleDesignation[];
  outputArea: SingleDesignation;
  comparableOutputArea?: SingleDesignation;
  printable?: boolean;
}

const WALES_MAX_DEPRIVATION = 953 * 2;
const ENGLAND_MAX_DEPRIVATION = 16_418 * 2;

/**
 * Renders the summary of deprivation attributes for the output area.
 *
 * In the case of output areas in Wales, they need to be compared to Wales specifically,
 * rather than England and Wales. Output areas in England can be compared to England and Wales.
 *
 * @returns The JSX element representing the deprivation attribute summary.
 */
function DeprivationAttributeSummary({
  allOutputAreas,
  outputArea,
  comparableOutputArea: comparableOutputAreaProp,
  printable = false,
}: DeprivationAttributeSummaryProps) {
  const { comparableOutputArea, maxValue } = useMemo(() => {
    const designationWales = allOutputAreas.find(
      (outputArea) =>
        outputArea.display_name === "Wales" && outputArea.sub_category_id === ConstraintsCategory.UK_DEMOGRAPHICS_REGION
    );

    const isWelshOutputArea = outputArea?.key_code.startsWith("W");

    return {
      comparableOutputArea:
        comparableOutputAreaProp?.sub_category_id === ConstraintsCategory.UK_DEMOGRAPHICS_COUNTRY && isWelshOutputArea
          ? designationWales
          : comparableOutputAreaProp,
      maxValue: isWelshOutputArea ? WALES_MAX_DEPRIVATION : ENGLAND_MAX_DEPRIVATION,
    };
  }, [allOutputAreas, comparableOutputAreaProp, outputArea?.key_code]);

  const colorAccessor = useDemographicsColourAccessor(outputArea, comparableOutputArea);

  const deprivationOutputAreas = useMemo(
    () => [outputArea, comparableOutputArea].filter(isDefined),
    [outputArea, comparableOutputArea]
  );

  return (
    <AttributeComparisonSummarySection
      heading="Deprivation"
      designation={outputArea}
      comparableDesignation={comparableOutputArea}
      headlineAttribute={ATTRS.DEPRIVATION_ATTRS[0]}
      printable={printable}
    >
      <AttributeSummaryTable
        printable={printable}
        attributes={ATTRS.DEPRIVATION_ATTRS.slice(0, 1)}
        designation={outputArea}
        comparativeDesignation={comparableOutputArea}
        colorAccessor={colorAccessor}
      />

      <LinearTrendChart
        printable={printable}
        colorAccessor={colorAccessor}
        outputAreas={deprivationOutputAreas}
        dataPoints={ATTRS.DEPRIVATION_ATTRS}
        labelY="IMD Rank"
        customRangeY={[1, maxValue]}
      />
      <span className="text-2xs text-text-base-tertiary">
        Derived from the Index of Multiple Deprivation - areas are ranked from the most deprived area (rank 1) to the
        least deprived area. A higher IMD Rank indicates lower deprivation.
      </span>
    </AttributeComparisonSummarySection>
  );
}
